import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import type { EditorProps } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { NodeSelection } from "prosemirror-state";

import {
  CustomTable,
  CustomTableCell,
  CustomTableHeader,
  CustomTableRow,
  CustomTextStyle,
  ResizableImage,
} from "@/components/documents/extensions";

export interface SharedEditorExtensionsOptions {
  /** When false, links do not navigate on click (artifact review flows). */
  linkOpenOnClick?: boolean;
}

export function getSharedEditorExtensions(
  options: SharedEditorExtensionsOptions = {},
) {
  const { linkOpenOnClick = true } = options;

  return [
    StarterKit.configure({
      link: false,
    }),
    TextAlign.configure({
      types: ["heading", "paragraph"],
      alignments: ["left", "center", "right", "justify"],
    }),
    CustomTextStyle,
    Highlight.configure({
      multicolor: true,
    }),
    ResizableImage.configure({
      inline: false,
      allowBase64: true,
    }),
    CustomTable.configure({
      resizable: true,
      allowTableNodeSelection: true,
    }),
    CustomTableRow,
    CustomTableCell,
    CustomTableHeader,
    Link.configure({
      openOnClick: linkOpenOnClick,
      autolink: true,
    }),
  ];
}

export interface SharedEditorPropsOptions {
  onFileUpload?: (file: File) => Promise<string>;
}

export function getSharedEditorProps(
  options: SharedEditorPropsOptions = {},
): Partial<EditorProps> {
  const { onFileUpload } = options;

  return {
    handleClickOn: (view, _pos, node, nodePos) => {
      if (node.type.name === "image") {
        const { state, dispatch } = view;
        dispatch(state.tr.setSelection(NodeSelection.create(state.doc, nodePos)));
        view.focus();
        return true;
      }
      return false;
    },
    handlePaste: (view, event) => {
      if (!onFileUpload) {
        return false;
      }

      const items = Array.from(event.clipboardData?.items || []);
      let imageFile: File | null = null;

      for (const item of items) {
        if (item.type.startsWith("image/")) {
          imageFile = item.getAsFile();
          break;
        }
      }

      if (!imageFile) {
        return false;
      }

      event.preventDefault();
      const { state } = view;
      const { selection } = state;

      const placeholderText = "Uploading image...";
      const transaction = state.tr.insertText(
        placeholderText,
        selection.from,
        selection.to,
      );
      view.dispatch(transaction);

      onFileUpload(imageFile)
        .then((url) => {
          const node = view.state.schema.nodes.image?.create({ src: url });
          if (!node) return;
          const replaceTransaction = view.state.tr.replaceWith(
            selection.from,
            selection.from + placeholderText.length,
            node,
          );
          view.dispatch(replaceTransaction);
        })
        .catch((error) => {
          console.error("Image paste upload failed:", error);
          const errorTransaction = view.state.tr.replaceWith(
            selection.from,
            selection.from + placeholderText.length,
            view.state.schema.text("[Image upload failed]"),
          );
          view.dispatch(errorTransaction);
        });

      return true;
    },
  };
}
